import clsx from "clsx";
import CheckBox from "../CheckBox/CheckBox";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import { MdCheck, MdClose, MdLightbulb } from "react-icons/md";
import { API_ENDPOINT_UPDATE_PROFILE, ICON_SIZES } from "src/helpers/constants";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import Modal from "../Modal/Modal";
import RadioGroup from "../RadioGroup/RadioGroup";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import PushSubscription from "../PushSubscription/PushSubscription";
import useCurrentUser from "src/hooks/useCurrentUser";
import useCache from "src/hooks/useCache";
import apiClient from "src/helpers/api-client";
import { objectHasChanges, showNotification } from "src/helpers/utils";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import ChangesPendingToSave from "../ChangesPendingToSave/ChangesPendingToSave";
import { EButtonKind } from "../Button/ButtonTypes";

interface INotificationSettings {
  isModal?: boolean;
  onModalClose?: () => void;
  showSubscriptionMessage?: boolean;
}

interface INotificationsOnChange {
  onChange: (data: any) => void;
  columns?: number;
}

export enum EPushNotifications {
  OVERDUE_TRANSACTIONS = "OVERDUE_TRANSACTIONS",
  UPCOMING_TRANSACTIONS = "UPCOMING_TRANSACTIONS",
  TODAY_TRANSACTIONS = "TODAY_TRANSACTIONS",
  PENDING_TRANSACTIONS = "PENDING_TRANSACTIONS",
  CONTACT_REQUESTS = "CONTACT_REQUESTS",
}

const PushNotificationsSettings: React.FC<INotificationsOnChange> = ({
  onChange,
  columns = 1,
}) => {
  const { settings } = useCurrentUser();
  const { t } = useTranslation();
  const [overdueTransactionsActive, setOverfueTransactionsActive] =
    useState<boolean>(
      !!settings?.notifications?.pushNotifications?.includes?.(
        EPushNotifications.OVERDUE_TRANSACTIONS
      )
    );
  const [upcomingTransactionsActive, setUpcomingTransactionsActive] =
    useState<boolean>(
      !!settings?.notifications?.pushNotifications?.includes?.(
        EPushNotifications.UPCOMING_TRANSACTIONS
      )
    );
  const [todayTransactionsActive, setTodayTransactionsActive] =
    useState<boolean>(
      !!settings?.notifications?.pushNotifications?.includes?.(
        EPushNotifications.TODAY_TRANSACTIONS
      )
    );
  const [pendingTransactionsActive, setPendingTransactionsActive] =
    useState<boolean>(
      !!settings?.notifications?.pushNotifications?.includes?.(
        EPushNotifications.PENDING_TRANSACTIONS
      )
    );
  const [contactRequestsActive, setContactRequestsActive] = useState<boolean>(
    !!settings?.notifications?.pushNotifications?.includes?.(
      EPushNotifications.CONTACT_REQUESTS
    )
  );

  useEffect(() => {
    onChange({
      overdueTransactionsActive,
      upcomingTransactionsActive,
      todayTransactionsActive,
      pendingTransactionsActive,
      contactRequestsActive,
    });
  }, [
    overdueTransactionsActive,
    upcomingTransactionsActive,
    todayTransactionsActive,
    pendingTransactionsActive,
    contactRequestsActive,
  ]);

  return (
    <div className={clsx(["flex", "w-full", "flex-col", "gap-4", "md:gap-6"])}>
      <div className={clsx(["flex", "w-full", "md:text-base", "text-sm"])}>
        {t("push_notifications.collapsible.description")}
      </div>
      <div
        className={clsx([
          "grid",
          "grid-cols-1",
          `md:grid-cols-${columns}`,
          "w-full",
          "gap-1",
        ])}
      >
        <CheckBox
          checked={overdueTransactionsActive}
          onChange={(isChecked: boolean) =>
            setOverfueTransactionsActive(isChecked)
          }
          labelClass="!font-light md:!text-base"
          label={t("push_notifications.collapsible.overdue_transactions")}
        />
        <CheckBox
          checked={upcomingTransactionsActive}
          onChange={(isChecked: boolean) =>
            setUpcomingTransactionsActive(isChecked)
          }
          labelClass="!font-light md:!text-base"
          label={t("push_notifications.collapsible.upcoming_transactions")}
        />
        <CheckBox
          checked={todayTransactionsActive}
          onChange={(isChecked: boolean) =>
            setTodayTransactionsActive(isChecked)
          }
          labelClass="!font-light md:!text-base"
          label={t("push_notifications.collapsible.today_transactions")}
        />
        <CheckBox
          checked={pendingTransactionsActive}
          onChange={(isChecked: boolean) =>
            setPendingTransactionsActive(isChecked)
          }
          labelClass="!font-light md:!text-base"
          label={t("push_notifications.collapsible.pending_transactions")}
        />
        <CheckBox
          checked={contactRequestsActive}
          onChange={(isChecked: boolean) => setContactRequestsActive(isChecked)}
          labelClass="!font-light md:!text-base"
          label={t("push_notifications.collapsible.contact_requests")}
        />
      </div>
    </div>
  );
};

const EmailNotificationsSettings: React.FC<INotificationsOnChange> = ({
  onChange,
}) => {
  const { settings } = useCurrentUser();
  const { t } = useTranslation();
  const [emailNotifications, setEmailNotifications] = useState<boolean>(
    !!settings?.notifications?.emailNotifications?.enabled
  );

  useEffect(() => {
    onChange({
      enabled: emailNotifications,
    });
  }, [emailNotifications]);

  return (
    <div className={clsx(["flex", "w-full", "flex-col", "gap-4", "md:gap-6"])}>
      <div className={clsx(["flex", "w-full", "md:text-base", "text-sm"])}>
        {t("email_notifications.collapsible.description")}
      </div>
      <RadioGroup
        onChange={(value: any) => setEmailNotifications(value)}
        selected={emailNotifications}
        options={[
          {
            value: true,
            label: t("email_notifications.collapsible.enable"),
          },
          {
            value: false,
            label: t("email_notifications.collapsible.disable"),
          },
        ]}
      />
    </div>
  );
};

const NotificationSettings: React.FC<INotificationSettings> = ({
  isModal = false,
  onModalClose,
  showSubscriptionMessage = false,
}) => {
  const { t } = useTranslation();
  const { settings } = useCurrentUser();
  const [isUpdatingSettings, setIsUpdatingSettings] = useState<boolean>(false);
  const { clearCache } = useCache();
  const [pushNotificationSettings, setPushNotificationSettings] =
    useState<any>(null);
  const [emailNotificationSettings, setEmailNotificationSettings] =
    useState<any>(null);
  const [showConfirmSaveDialog, setShowConfirmSaveDialog] =
    useState<boolean>(false);

  const updateProfileSettings = async () => {
    setIsUpdatingSettings(true);

    const formData = new FormData();

    formData.append(
      "settings",
      JSON.stringify({
        ...settings,
        notifications: {
          ...(settings?.notifications || {}),
          pushNotifications: selectedNotifications,
          emailNotifications: emailNotificationSettings,
        },
      })
    );

    return apiClient
      .put(API_ENDPOINT_UPDATE_PROFILE, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(async (res: any) => {
        await clearCache();

        if (isModal) {
          onModalClose?.();
        }

        showNotification({
          kind: "success",
          message: "Cambios guardados",
        });
        setShowConfirmSaveDialog(false);
      })
      .catch((e) => {
        showNotification({
          message: "Error guardando los cambios",
          kind: "error",
        });
      })
      .finally(() => {
        setIsUpdatingSettings(false);
      });
  };

  const selectedNotifications = useMemo(() => {
    let pushNotifications = [];

    if (pushNotificationSettings?.contactRequestsActive) {
      pushNotifications.push(EPushNotifications.CONTACT_REQUESTS);
    }
    if (pushNotificationSettings?.overdueTransactionsActive) {
      pushNotifications.push(EPushNotifications.OVERDUE_TRANSACTIONS);
    }

    if (pushNotificationSettings?.pendingTransactionsActive) {
      pushNotifications.push(EPushNotifications.PENDING_TRANSACTIONS);
    }
    if (pushNotificationSettings?.todayTransactionsActive) {
      pushNotifications.push(EPushNotifications.TODAY_TRANSACTIONS);
    }
    if (pushNotificationSettings?.upcomingTransactionsActive) {
      pushNotifications.push(EPushNotifications.UPCOMING_TRANSACTIONS);
    }

    return pushNotifications;
  }, [pushNotificationSettings]);

  const hasChanges = useMemo(() => {
    return objectHasChanges({
      original: {
        email: !!settings?.notifications?.emailNotifications?.enabled,
        notifications: settings?.notifications?.pushNotifications || [],
      },
      edited: {
        email: !!emailNotificationSettings?.enabled,
        notifications: selectedNotifications,
      },
    });
  }, [selectedNotifications, settings, emailNotificationSettings]);

  return (
    <>
      {isModal ? (
        <Modal
          onEscape={() => {
            if (!isUpdatingSettings) {
              onModalClose?.();
            }
          }}
          className="md:!w-[550px]"
        >
          <Modal.Header
            onClose={() => {
              if (!isUpdatingSettings) {
                onModalClose?.();
              }
            }}
            title={t("push_notifications.modal_title")}
          />
          <Modal.Body>
            <div className={clsx(["flex", "w-full", "gap-6", "flex-col"])}>
              {showSubscriptionMessage && <PushSubscription />}
              <CollapsibleBlock
                title={t("push_notifications.collapsible.title")}
              >
                <div className={clsx(["flex", "w-full", "gap-4", "flex-col"])}>
                  <PushNotificationsSettings
                    onChange={(data: any) => {
                      setPushNotificationSettings(data);
                    }}
                    columns={2}
                  />
                  <div className="md:text-sm text-xs">
                    <p>
                      <MdLightbulb
                        size={ICON_SIZES.XS}
                        className="inline mr-1"
                      />
                      {t("push_notifications.help_text")}
                    </p>
                  </div>
                </div>
              </CollapsibleBlock>
              <CollapsibleBlock
                title={t("email_notifications.collapsible.title")}
              >
                <EmailNotificationsSettings
                  onChange={(data: any) => {
                    setEmailNotificationSettings(data);
                  }}
                />
              </CollapsibleBlock>
            </div>
          </Modal.Body>
          <Modal.Footer
            leftContent={<ChangesPendingToSave show={hasChanges} />}
            actions={[
              {
                kind: EButtonKind.SAVE,
                icon: <MdCheck size={ICON_SIZES.MD} />,
                onClick: () => setShowConfirmSaveDialog(true),
                text: t("common_buttons.update"),
                isLoading: isUpdatingSettings,
                disabled: isUpdatingSettings || !hasChanges,
              },
              {
                text: t("common_buttons.close"),
                onClick: () => {
                  if (!isUpdatingSettings) {
                    onModalClose?.();
                  }
                },
                kind: EButtonKind.NORMAL,
                icon: <MdClose size={ICON_SIZES.MD} />,
                disabled: isUpdatingSettings,
              },
            ]}
          />
        </Modal>
      ) : (
        <>
          {showSubscriptionMessage && <PushSubscription />}
          <CollapsibleBlock
            title={t("push_notifications.collapsible.title")}
            blocked
          >
            <PushNotificationsSettings
              onChange={(data: any) => {
                setPushNotificationSettings(data);
              }}
            />
          </CollapsibleBlock>
          <CollapsibleBlock
            title={t("email_notifications.collapsible.title")}
            blocked
          >
            <EmailNotificationsSettings
              onChange={(data: any) => {
                setEmailNotificationSettings(data);
              }}
            />
          </CollapsibleBlock>
          <div
            className={clsx([
              "flex",
              "flex-row",
              "gap-4",
              "items-center",
              "justify-between",
            ])}
          >
            <ChangesPendingToSave show={hasChanges} />
            <div className={clsx(["flex", "w-full", "justify-end"])}>
              <ButtonGroup
                buttons={[
                  {
                    kind: EButtonKind.SAVE,
                    icon: <MdCheck size={ICON_SIZES.MD} />,
                    onClick: () => setShowConfirmSaveDialog(true),
                    text: t("common_buttons.update"),
                    disabled: !hasChanges,
                  },
                ]}
              />
            </div>
          </div>
        </>
      )}
      {showConfirmSaveDialog && (
        <ConfirmDialog
          description={t("common_dialogs.save_changes")}
          title={t("common_dialog_titles.update")}
          onClose={() => {
            setShowConfirmSaveDialog(false);
          }}
          onConfirm={async () => {
            await updateProfileSettings();
          }}
          confirmKind={EButtonKind.SAVE}
          isLoading={isUpdatingSettings}
        />
      )}
    </>
  );
};

export default NotificationSettings;
